/**
 * Single source of truth for omdömestal (antal recensioner och betyg) per salong.
 *
 * REGEL: skriv aldrig ett betyg eller ett antal omdömen som literal i en
 * sidkomponent, i metadata eller i JSON-LD. Läs alltid härifrån. Talen används
 * både i synlig text och i `aggregateRating` i LocalBusiness-schemat — Google
 * kräver att de stämmer överens och att de motsvarar verkliga omdömen.
 *
 * RUTIN vid uppdatering:
 *  1. Hämta antal + snitt från Google Business Profile och från BokaDirekt.
 *  2. Fyll i `google` och `bokadirekt` med källdatum i `checkedAt` (ISO-datum).
 *  3. Visa alltid källorna var för sig. Skapa aldrig ett sammanvägt totalbetyg.
 *  4. Sätt `verified: true` först när båda källorna är avstämda samma dag.
 *
 * Salonger med `verified: false` har tal som ärvts från tidigare handpåskrivna
 * värden och som INTE är avstämda mot källorna. De ska stämmas av och ersättas.
 */
import type { SalonSlug } from "./types";

export interface SourceStat {
  /** Genomsnittligt betyg, 1–5. */
  rating: number;
  /** Antal publika omdömen. */
  count: number;
}

export interface SalonReviewStats {
  /** Google Business Profile. */
  google: SourceStat;
  /** BokaDirekt. */
  bokadirekt: SourceStat;
  /** ISO-datum då talen senast stämdes av mot källorna. */
  checkedAt: string;
  /** true = båda källorna kontrollerade mot verkliga siffror. */
  verified: boolean;
  /** Fritext om varifrån talen kommer eller vad som saknas. */
  note?: string;
}

export const salonReviewStats: Record<SalonSlug, SalonReviewStats> = {
  taby: {
    // BokaDirekt AVSTÄMD 2026-09-14: 4,8 / 3 988 (skärmdump från profilen).
    // Google EJ AVSTÄMD: ärvt handpåskrivet tal. Ägaren hämtar Google-siffrorna.
    google: { rating: 4.6, count: 583 },
    bokadirekt: { rating: 4.8, count: 3988 },
    checkedAt: "2026-09-14",
    verified: false,
    note: "BokaDirekt kontrollerad 2026-09-14. Google-siffran är fortfarande ärvd.",
  },
  "ahlens-city": {
    // BokaDirekt AVSTÄMD 2026-09-14: 4,8 / 2 125.
    // Google EJ AVSTÄMD: ärvt handpåskrivet tal. Ägaren hämtar Google-siffrorna.
    google: { rating: 4.8, count: 348 },
    bokadirekt: { rating: 4.8, count: 2125 },
    checkedAt: "2026-09-14",
    verified: false,
    note: "BokaDirekt kontrollerad 2026-09-14. Väntar på kontrollerade Google-siffror.",
  },
  akersberga: {
    // Google avstämd 2026-09-12. BokaDirekt AVSTÄMD 2026-09-14: 4,6 / 1 842.
    google: { rating: 4.8, count: 219 },
    bokadirekt: { rating: 4.6, count: 1842 },
    checkedAt: "2026-09-14",
    verified: true,
    note: "Google kontrollerad 2026-09-12. BokaDirekt kontrollerad 2026-09-14.",
  },
  arninge: {
    // BokaDirekt AVSTÄMD 2026-09-14: 4,8 / 1 625.
    // Google EJ AVSTÄMD: ärvt handpåskrivet tal. Ägaren hämtar Google-siffrorna.
    google: { rating: 4.9, count: 140 },
    bokadirekt: { rating: 4.8, count: 1625 },
    checkedAt: "2026-09-14",
    verified: false,
    note: "BokaDirekt kontrollerad 2026-09-14. Väntar på kontrollerade Google-siffror.",
  },
};

/** Google-betyg för en salong, används i Salon-kompatibilitetsfält och schema. */
export function ratingFor(slug: SalonSlug): number {
  return salonReviewStats[slug].google.rating;
}

/** Antal Google-omdömen för en salong. */
export function reviewCountFor(slug: SalonSlug): number {
  return salonReviewStats[slug].google.count;
}

/** BokaDirekt-betyg för en salong. */
export function bokadirektRatingFor(slug: SalonSlug): number {
  return salonReviewStats[slug].bokadirekt.rating;
}

/** Antal BokaDirekt-omdömen för en salong. */
export function bokadirektCountFor(slug: SalonSlug): number {
  return salonReviewStats[slug].bokadirekt.count;
}

/** "4,6" — svensk decimalkomma. */
export function formatRating(rating: number): string {
  return rating.toFixed(1).replace(".", ",");
}

/** "2 000" — svenskt tusentalsavstånd. */
export function formatCount(count: number): string {
  return count.toLocaleString("sv-SE");
}
